import { useState, useEffect } from 'react';
import { Clock, Mail, Calendar, Timer, Save } from 'lucide-react';
import { useSequenceSettings, useUpdateSequenceSettings } from '@/hooks/useSequences';
import { Button, Card, CardHeader, CardBody, Input, Select, Skeleton } from '@solid/ui';
import { cn } from '@/lib/utils';

const TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'UTC',
];

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function formatHour(hour: number): string {
  if (hour === 0) return '12:00 AM';
  if (hour === 12) return '12:00 PM';
  if (hour < 12) return `${hour}:00 AM`;
  return `${hour - 12}:00 PM`;
}

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => ({
  value: String(i),
  label: formatHour(i),
}));

const TIMEZONE_OPTIONS = TIMEZONES.map((tz) => ({ value: tz, label: tz.replace(/_/g, ' ') }));

export default function SequenceSchedulingSettings() {
  const { data: settings, isLoading } = useSequenceSettings();
  const updateSettings = useUpdateSequenceSettings();

  const [form, setForm] = useState({
    maxPerHour: 4,
    businessHourStart: 9,
    businessHourEnd: 17,
    timezone: 'America/Chicago',
    businessDays: [1, 2, 3, 4, 5] as number[],
    processNowDelayMs: 30000,
  });
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (settings) {
      setForm({
        maxPerHour: settings.maxPerHour,
        businessHourStart: settings.businessHourStart,
        businessHourEnd: settings.businessHourEnd,
        timezone: settings.timezone,
        businessDays: settings.businessDays,
        processNowDelayMs: settings.processNowDelayMs,
      });
      setHasChanges(false);
    }
  }, [settings]);

  const updateField = (field: string, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const toggleDay = (day: number) => {
    const days = form.businessDays.includes(day)
      ? form.businessDays.filter((d) => d !== day)
      : [...form.businessDays, day].sort();
    if (days.length === 0) return; // Must have at least 1 day
    updateField('businessDays', days);
  };

  const handleSave = async () => {
    await updateSettings.mutateAsync(form);
    setHasChanges(false);
  };

  if (isLoading) {
    return <Skeleton className="h-64 rounded-xl" />;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Sequence Scheduling"
          subtitle="Configure when and how fast automated sequence emails are sent."
          actions={
            <Button
              onClick={handleSave}
              disabled={!hasChanges}
              loading={updateSettings.isPending}
            >
              <Save className="w-4 h-4" />
              Save Changes
            </Button>
          }
        />

        <CardBody>
        <div className="space-y-8">
          {/* Trickle Rate */}
          <section>
            <h3 className="text-sm font-medium text-secondary mb-3 flex items-center gap-2">
              <Mail className="w-4 h-4 text-grey-400" />
              Trickle Rate
            </h3>
            <Input
              label="Max emails per hour"
              type="number"
              value={form.maxPerHour.toString()}
              onChange={(e) => updateField('maxPerHour', Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))}
              className="max-w-xs"
            />
            <p className="text-xs text-grey-400 mt-2">
              Limits how many sequence emails are sent per hour to avoid spam flags. The scheduler checks every 2 minutes.
            </p>
          </section>

          {/* Business Hours */}
          <section>
            <h3 className="text-sm font-medium text-secondary mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4 text-grey-400" />
              Business Hours
            </h3>
            <div className="grid grid-cols-2 gap-4 max-w-lg">
              <Select
                label="Start"
                value={String(form.businessHourStart)}
                onChange={(e) => updateField('businessHourStart', parseInt(e.target.value))}
                options={HOUR_OPTIONS}
              />
              <Select
                label="End"
                value={String(form.businessHourEnd)}
                onChange={(e) => updateField('businessHourEnd', parseInt(e.target.value))}
                options={HOUR_OPTIONS}
              />
            </div>
            <div className="mt-4 max-w-xs">
              <Select
                label="Timezone"
                value={form.timezone}
                onChange={(e) => updateField('timezone', e.target.value)}
                options={TIMEZONE_OPTIONS}
              />
            </div>
            <p className="text-xs text-grey-400 mt-2">
              Emails are only sent during business hours. Currently: {formatHour(form.businessHourStart)} &ndash; {formatHour(form.businessHourEnd)} {form.timezone.replace(/_/g, ' ')}
            </p>
          </section>

          {/* Business Days */}
          <section>
            <h3 className="text-sm font-medium text-secondary mb-3 flex items-center gap-2">
              <Calendar className="w-4 h-4 text-grey-400" />
              Business Days
            </h3>
            <div className="flex gap-2">
              {DAY_LABELS.map((label, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => toggleDay(i)}
                  className={cn(
                    'w-11 h-11 rounded-full text-sm font-medium transition-colors',
                    form.businessDays.includes(i)
                      ? 'bg-primary text-white'
                      : 'bg-grey-100 text-grey-500 hover:bg-grey-200',
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
            <p className="text-xs text-grey-400 mt-2">
              Emails are only sent on selected days. Click to toggle.
            </p>
          </section>

          {/* Process Now Delay */}
          <section>
            <h3 className="text-sm font-medium text-secondary mb-3 flex items-center gap-2">
              <Timer className="w-4 h-4 text-grey-400" />
              Process Now Delay
            </h3>
            <Input
              label="Delay between sends (ms)"
              type="number"
              value={form.processNowDelayMs.toString()}
              onChange={(e) => updateField('processNowDelayMs', Math.max(0, parseInt(e.target.value) || 0))}
              className="max-w-xs"
            />
            <p className="text-xs text-grey-400 mt-2">
              When using "Send Now", this delay is added between each email to avoid spam flags.
              Currently: {(form.processNowDelayMs / 1000).toFixed(1)} seconds
            </p>
          </section>
        </div>

        {hasChanges && (
          <div className="mt-8 pt-4 border-t border-grey-200 flex justify-end">
            <Button onClick={handleSave} loading={updateSettings.isPending}>
              <Save className="w-4 h-4" />
              Save Changes
            </Button>
          </div>
        )}
        </CardBody>
      </Card>
    </div>
  );
}
